import { useForm, FormProvider } from "react-hook-form";
import { useUserInfoStore } from "@/stores/userInfoStore";
import { useState, useEffect, useRef } from "react";
import MarketingModalBanner from "@/assets/images/marketing_modal_banner.png";
import {
  BTN_SIZE,
  BTN_VARIANTS,
  RESPONSE_MESSAGES,
} from "@/constants/components";
import { Image } from "@/components/Image";
import { FormField } from "@/components/FormField";
import { routes } from "@/constants/routes";
import CircleCloseIcon from "@/assets/images/close_circle.svg";
import {
  MARKETING_CONSENT,
  MARKETING_CONSENT_EXPIRY_IN_MILLISECONDS,
} from "@/constants/general";
import useAuth from "@/hooks/useAuth";
import { Button } from "@/components/Button";
import {
  MarketingConsentPayload,
  MarketingConsentPopupProps,
} from "@/models/marketingConsent";
import { saveMarketingConsent } from "@/services/consent.service";
import { CONTENT } from "@/constants/dataLayer";
import { pushToDataLayer } from "@/core/utils/utils.helper";
import styles from "./MarketingConsentPopup.module.scss";
import asyncComponentLoader from "@/core/loader";
import { setMarketingConsentCookie } from "@/helpers/marketingConsent.helper";
import { responseMessage } from "@/helpers/errorHandling.helper";
import { pushAppsFlyerGtmEvent } from "@/core/utils/appsflyer-gtm.helper";
import { APPSFLYER_EVENTS } from "@/constants/appsflyer";
const SuccessScreen = asyncComponentLoader(() =>
  import("@/components/SuccessScreen").then(({ SuccessScreen }) => ({
    default: SuccessScreen,
  })),
);

export const MarketingConsentPopup = ({
  checkboxConfig,
  emailIdConfig,
  hideModal,
}: MarketingConsentPopupProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const methods = useForm({ mode: "onSubmit" });
  const { handleSubmit } = methods;
  const auth: any = useAuth();
  const consentAcceptedTracked = useRef(false); // Track if consent accepted event was fired
  const isLoggedIn: boolean = auth?.isAuthenticated;
  const loggedInUserData = useUserInfoStore((s) => s.userInfo);

  // Auto-close modal after 2 seconds when success screen is shown
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        hideModal();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, hideModal]);

  const saveMarketingConsentData = async (formData: any) => {
    try {
      setIsLoading(true);
      let payload: MarketingConsentPayload;
      if (isLoggedIn && loggedInUserData?.email && checkboxConfig) {
        payload = {
          consent: true,
          email: loggedInUserData.email,
          description: "",
        };
      } else if (!isLoggedIn) {
        const email: string = formData?.[emailIdConfig.name];
        payload = {
          consent: true,
          email,
          description: "",
        };
      } else {
        return;
      }
      const response = await saveMarketingConsent(payload);
      if (response?.status === 200) {
        setShowSuccess(true);

        if (!consentAcceptedTracked.current) {
          pushAppsFlyerGtmEvent(APPSFLYER_EVENTS.MARKETING_CONSENT_ACCEPTED, {
            timestamp: Date.now(),
          });
          consentAcceptedTracked.current = true;
        }

        if (!isLoggedIn) {
          setMarketingConsentCookie(
            false,
            true,
            MARKETING_CONSENT_EXPIRY_IN_MILLISECONDS.LOGGED_OUT_USER,
          );
        }
      }
    } catch (error) {
      setIsLoading(false);
      responseMessage(RESPONSE_MESSAGES.ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  // To close modal and store cookie
  const handleCloseModal = () => {
    setMarketingConsentCookie(
      isLoggedIn,
      false,
      isLoggedIn
        ? MARKETING_CONSENT_EXPIRY_IN_MILLISECONDS.LOGGED_IN_USER
        : MARKETING_CONSENT_EXPIRY_IN_MILLISECONDS.LOGGED_OUT_USER_MODAL_CLOSE,
    );
    hideModal();
  };

  return (
    <FormProvider {...methods}>
      <div className={styles.marketing_popup}>
        <Image source={MarketingModalBanner} className={styles.banner_image} />
        <div className={styles.content_section}>
          <span className={styles.divider_right}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="50px"
              height="50px"
              viewBox="3 -2.5 50 50"
              version="1.1"
            >
              <g id="surface1" fill="#fff">
                <path d="M 2.640625 0.789062 C 2.625 0.828125 2.617188 11.433594 2.625 24.363281 L 2.640625 47.863281 L 25.488281 47.863281 C 40.675781 47.863281 48.332031 47.84375 48.332031 47.804688 C 48.332031 47.773438 48.175781 47.734375 47.988281 47.710938 C 47.792969 47.6875 47.476562 47.648438 47.277344 47.617188 C 47.078125 47.585938 46.714844 47.554688 46.472656 47.550781 C 46.226562 47.550781 46.003906 47.527344 45.972656 47.507812 C 45.945312 47.488281 45.691406 47.445312 45.417969 47.40625 C 45.144531 47.371094 44.84375 47.328125 44.75 47.3125 C 44.660156 47.289062 44.386719 47.253906 44.136719 47.222656 C 43.539062 47.15625 43.027344 47.058594 42.699219 46.964844 C 42.554688 46.925781 42.328125 46.886719 42.199219 46.886719 C 42.078125 46.886719 41.910156 46.847656 41.832031 46.808594 C 41.753906 46.761719 41.617188 46.730469 41.515625 46.722656 C 41.421875 46.722656 41.273438 46.695312 41.183594 46.660156 C 41.101562 46.628906 40.917969 46.578125 40.777344 46.554688 C 40.382812 46.476562 39.828125 46.34375 39.527344 46.257812 C 39.042969 46.117188 38.648438 46.003906 38.386719 45.9375 C 38.25 45.898438 38.058594 45.839844 37.960938 45.800781 C 37.867188 45.753906 37.75 45.722656 37.699219 45.722656 C 37.65625 45.722656 37.53125 45.691406 37.433594 45.644531 C 37.332031 45.597656 37.097656 45.519531 36.917969 45.472656 C 36.730469 45.417969 36.5 45.339844 36.398438 45.300781 C 36.300781 45.257812 36.171875 45.222656 36.109375 45.222656 C 36.054688 45.222656 35.9375 45.1875 35.847656 45.144531 C 35.550781 44.992188 35.273438 44.890625 35.179688 44.890625 C 35.128906 44.890625 35.03125 44.851562 34.976562 44.804688 C 34.914062 44.761719 34.820312 44.722656 34.773438 44.722656 C 34.714844 44.722656 34.605469 44.6875 34.519531 44.644531 C 34.433594 44.597656 34.25 44.519531 34.113281 44.472656 C 33.816406 44.355469 33.527344 44.230469 33.324219 44.128906 C 33.246094 44.089844 33.152344 44.054688 33.113281 44.054688 C 33.082031 44.054688 32.882812 43.976562 32.679688 43.882812 C 32.472656 43.78125 32.234375 43.671875 32.136719 43.632812 C 31.925781 43.542969 31.164062 43.175781 31.054688 43.113281 C 31.011719 43.082031 30.785156 42.972656 30.558594 42.863281 C 30.328125 42.75 30.101562 42.640625 30.054688 42.609375 C 30.011719 42.585938 29.78125 42.472656 29.554688 42.359375 C 29.082031 42.132812 29.015625 42.097656 28.859375 42.011719 C 28.800781 41.972656 28.699219 41.917969 28.640625 41.886719 C 28.578125 41.855469 28.457031 41.785156 28.363281 41.722656 C 28.269531 41.660156 28.160156 41.601562 28.128906 41.582031 C 28.089844 41.566406 28.019531 41.542969 27.988281 41.527344 C 27.949219 41.511719 27.84375 41.449219 27.75 41.390625 C 27.660156 41.328125 27.53125 41.257812 27.472656 41.222656 C 27.410156 41.1875 27.289062 41.117188 27.195312 41.054688 C 27.105469 40.992188 26.980469 40.921875 26.917969 40.886719 C 26.855469 40.855469 26.730469 40.785156 26.636719 40.722656 C 26.550781 40.660156 26.421875 40.589844 26.359375 40.558594 C 26.199219 40.472656 26.050781 40.378906 25.828125 40.207031 C 25.714844 40.125 25.613281 40.054688 25.589844 40.054688 C 25.550781 40.054688 24.363281 39.25 24.105469 39.050781 C 23.996094 38.960938 23.882812 38.890625 23.867188 38.890625 C 23.84375 38.890625 23.800781 38.859375 23.761719 38.835938 C 23.722656 38.800781 23.421875 38.570312 23.082031 38.324219 C 22.75 38.074219 22.449219 37.839844 22.429688 37.792969 C 22.40625 37.757812 22.355469 37.722656 22.320312 37.722656 C 22.289062 37.722656 22.148438 37.628906 22.011719 37.511719 C 21.871094 37.398438 21.59375 37.171875 21.394531 37.011719 C 21.195312 36.855469 20.925781 36.632812 20.804688 36.527344 C 20.683594 36.421875 20.480469 36.261719 20.363281 36.164062 C 20.238281 36.070312 19.921875 35.785156 19.65625 35.519531 C 19.386719 35.265625 19.15625 35.054688 19.136719 35.054688 C 19.117188 35.054688 18.726562 34.699219 18.269531 34.269531 C 17.523438 33.554688 16.792969 32.792969 15.675781 31.5625 C 15.472656 31.339844 15.273438 31.121094 15.234375 31.078125 C 15.195312 31.039062 15.03125 30.84375 14.871094 30.652344 C 14.519531 30.222656 14.332031 30.011719 14.207031 29.875 C 14.15625 29.820312 14.113281 29.753906 14.113281 29.726562 C 14.113281 29.699219 14.074219 29.644531 14.015625 29.605469 C 13.96875 29.558594 13.738281 29.277344 13.511719 28.976562 C 13.285156 28.679688 13.050781 28.382812 12.988281 28.324219 C 12.933594 28.269531 12.886719 28.199219 12.886719 28.175781 C 12.886719 28.152344 12.789062 28.019531 12.667969 27.882812 C 12.546875 27.75 12.445312 27.617188 12.445312 27.59375 C 12.445312 27.566406 12.332031 27.398438 12.195312 27.222656 C 12.054688 27.042969 11.941406 26.886719 11.941406 26.863281 C 11.941406 26.824219 11.730469 26.527344 11.429688 26.144531 C 11.359375 26.054688 11.296875 25.957031 11.28125 25.921875 C 11.273438 25.886719 11.195312 25.769531 11.109375 25.667969 C 11.027344 25.558594 10.945312 25.433594 10.925781 25.386719 C 10.90625 25.34375 10.839844 25.226562 10.777344 25.140625 C 10.714844 25.046875 10.636719 24.914062 10.605469 24.84375 C 10.566406 24.777344 10.519531 24.722656 10.496094 24.722656 C 10.472656 24.722656 10.441406 24.660156 10.421875 24.582031 C 10.402344 24.503906 10.363281 24.441406 10.332031 24.441406 C 10.300781 24.441406 10.277344 24.417969 10.277344 24.386719 C 10.277344 24.347656 10.207031 24.214844 10.125 24.089844 C 10.039062 23.96875 9.949219 23.820312 9.929688 23.777344 C 9.90625 23.734375 9.839844 23.617188 9.777344 23.527344 C 9.714844 23.433594 9.644531 23.304688 9.617188 23.238281 C 9.589844 23.175781 9.535156 23.105469 9.503906 23.082031 C 9.472656 23.066406 9.441406 23.003906 9.441406 22.949219 C 9.441406 22.894531 9.410156 22.824219 9.371094 22.789062 C 9.332031 22.757812 9.207031 22.546875 9.089844 22.320312 C 8.976562 22.097656 8.800781 21.769531 8.695312 21.582031 C 8.285156 20.832031 7.445312 19.117188 7.445312 19.015625 C 7.445312 18.984375 7.40625 18.929688 7.359375 18.890625 C 7.316406 18.851562 7.277344 18.761719 7.277344 18.6875 C 7.277344 18.621094 7.253906 18.550781 7.226562 18.53125 C 7.148438 18.484375 6.777344 17.648438 6.777344 17.53125 C 6.777344 17.488281 6.738281 17.429688 6.695312 17.390625 C 6.652344 17.351562 6.613281 17.273438 6.613281 17.210938 C 6.613281 17.148438 6.578125 17.035156 6.535156 16.957031 C 6.488281 16.871094 6.417969 16.695312 6.359375 16.554688 C 6.3125 16.417969 6.234375 16.226562 6.1875 16.140625 C 6.148438 16.042969 6.101562 15.90625 6.078125 15.816406 C 6.0625 15.730469 6.023438 15.652344 5.992188 15.636719 C 5.964844 15.613281 5.945312 15.558594 5.945312 15.511719 C 5.945312 15.460938 5.90625 15.34375 5.863281 15.257812 C 5.816406 15.171875 5.777344 15.054688 5.777344 15.007812 C 5.777344 14.949219 5.738281 14.859375 5.691406 14.800781 C 5.652344 14.742188 5.613281 14.644531 5.613281 14.582031 C 5.613281 14.519531 5.574219 14.402344 5.527344 14.308594 C 5.480469 14.230469 5.445312 14.101562 5.445312 14.035156 C 5.445312 13.96875 5.40625 13.859375 5.363281 13.800781 C 5.316406 13.742188 5.277344 13.644531 5.277344 13.582031 C 5.277344 13.523438 5.238281 13.398438 5.195312 13.308594 C 5.148438 13.230469 5.109375 13.113281 5.109375 13.058594 C 5.109375 13.011719 5.070312 12.902344 5.027344 12.8125 C 4.984375 12.726562 4.945312 12.601562 4.945312 12.53125 C 4.945312 12.472656 4.90625 12.34375 4.859375 12.253906 C 4.816406 12.171875 4.777344 12.035156 4.777344 11.949219 C 4.773438 11.871094 4.738281 11.746094 4.695312 11.667969 C 4.648438 11.589844 4.617188 11.449219 4.613281 11.355469 C 4.613281 11.265625 4.574219 11.140625 4.527344 11.078125 C 4.480469 11.023438 4.441406 10.882812 4.441406 10.777344 C 4.441406 10.675781 4.402344 10.519531 4.363281 10.445312 C 4.316406 10.367188 4.285156 10.238281 4.277344 10.15625 C 4.277344 10.070312 4.238281 9.90625 4.195312 9.792969 C 4.152344 9.675781 4.105469 9.480469 4.089844 9.347656 C 4.078125 9.210938 4.039062 9.078125 4.011719 9.039062 C 3.984375 9 3.945312 8.820312 3.929688 8.644531 C 3.910156 8.464844 3.867188 8.289062 3.839844 8.261719 C 3.804688 8.230469 3.777344 8.097656 3.777344 7.964844 C 3.777344 7.839844 3.742188 7.640625 3.703125 7.523438 C 3.660156 7.398438 3.609375 7.144531 3.59375 6.949219 C 3.570312 6.753906 3.53125 6.566406 3.503906 6.535156 C 3.484375 6.5 3.445312 6.277344 3.421875 6.039062 C 3.40625 5.800781 3.367188 5.558594 3.332031 5.5 C 3.304688 5.445312 3.277344 5.289062 3.277344 5.15625 C 3.277344 5.023438 3.246094 4.738281 3.199219 4.527344 C 3.152344 4.308594 3.105469 3.898438 3.082031 3.609375 C 3.058594 3.324219 3.019531 3.058594 2.996094 3.019531 C 2.964844 2.988281 2.949219 2.886719 2.957031 2.800781 C 2.988281 2.46875 2.878906 1.078125 2.804688 0.898438 C 2.722656 0.710938 2.679688 0.675781 2.640625 0.789062 Z M 2.640625 0.789062 " />
              </g>
            </svg>
          </span>
          {showSuccess ? (
            <SuccessScreen
              title={MARKETING_CONSENT.SUCCESS_TITLE}
              typographyVariant="h5"
              typographyClassName={styles.success_heading}
            />
          ) : (
            <>
              <div className={styles.main_content}>
                <h2 className={styles.heading}>{MARKETING_CONSENT.TITLE}</h2>
                {isLoggedIn ? (
                  checkboxConfig && <FormField config={checkboxConfig} />
                ) : (
                  <p className={styles.sub_text}>
                    {MARKETING_CONSENT.LOGGEDOUT_DESCRIPTION}
                  </p>
                )}
              </div>
              <div
                className={`${styles.bottom_section}${!isLoggedIn ? ` ${styles.with_email}` : ""}`}
              >
                {!isLoggedIn && <FormField config={emailIdConfig} />}
                <div className={styles.action_btn_section}>
                  <Button
                    size={BTN_SIZE.LARGE}
                    type={BTN_VARIANTS.PRIMARY}
                    onClick={() => {
                      handleSubmit(saveMarketingConsentData)();
                      pushToDataLayer({
                        ...CONTENT.MARKETING_POPUP.JOIN_NOW,
                        user_status: isLoggedIn,
                      });
                    }}
                    className={styles.btn_submit}
                    loading={isLoading}
                  >
                    {isLoggedIn
                      ? MARKETING_CONSENT.CTA_BUTTON_TEXT_LOGGED_IN
                      : MARKETING_CONSENT.CTA_BUTTON_TEXT_LOGGED_OUT}
                  </Button>
                </div>
              </div>
            </>
          )}

          <div className={styles.footer_section}>
            <p className={styles.footer_text}>
              {MARKETING_CONSENT.FOOTER.TEXT}
              <Button
                size={BTN_SIZE.NEUTRAL}
                type={BTN_VARIANTS.NEUTRAL}
                href={routes.privacyStatement.path}
                className={styles.footer_link}
              >
                {MARKETING_CONSENT.FOOTER.LINK_TEXT}
              </Button>
            </p>
          </div>
        </div>
        <Button
          size={BTN_SIZE.SMALL}
          type={BTN_VARIANTS.NEUTRAL}
          className={styles.close_icon_btn}
          onClick={() => {
            handleCloseModal();
            pushToDataLayer({
              ...CONTENT.MARKETING_POPUP.CLOSE_BTN,
              user_status: isLoggedIn,
            });
          }}
        >
          <Image source={CircleCloseIcon} className={styles.close_icon} />
        </Button>
      </div>
    </FormProvider>
  );
};
